package ui.campaign.detailv2.preview

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.items
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import api.CampaignState
import api.CampaignType
import api.VisualRotaryMode
import org.ssoft.common.tools.nowDate
import playlist.ImplPlaylist
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.DataState
import tools.rememberString
import ui.app.assign.detail.AppDetailArgs
import ui.campaign.detailv2.preview.CampaignPreviewScope.Wrapper
import ui.campaign.detailv2.widgets.*
import ui.device.detail.DeviceArgs
import ui.location.detail.LocationArgs
import ui.report.campaign.ReportCampaignArgs
import ui.resource.detail.ResourceArgs
import ui.widget.*
import kotlin.time.Clock
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.milliseconds

data class CampaignPreviewArgs(
    var companyId: Long = -1,
    var campaignId: Long = -1,
    var campaignGroupId: Long = -1,
)

@Composable
@ComposableNavNode(CampaignPreviewArgs::class)
fun CampaignPreviewUI() {
    with(CampaignPreviewScope.rememberScope()) {
        Box(Modifier.fillMaxSize()) {
            Crossfade(
                targetState = vm.state,
                modifier = Modifier.fillMaxSize()
            ) {
                when (val state = it) {
                    is DataState.Error<*> -> ErrUI()
                    is DataState.Idle<*>,
                    is DataState.Loading<*> -> LoadingUI()

                    is DataState.Result<Wrapper> -> ContentUI(state.nnvl)
                }
            }
        }
    }
}

@Composable
private fun CampaignPreviewScope.ContentUI(nnvl: Wrapper) {

    val deleteState = DialogState.remember()
    val editState = DialogState.remember()
    val pauseState = DialogState.remember()
    val resumeState = DialogState.remember()
    val stopState = DialogState.remember()

    DialogDelete(deleteState)
    DialogEdit(editState)
    DialogPause(pauseState)
    DialogResume(resumeState)
    DialogStop(stopState)

    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = Res.string.layer_top_detail_preview__title.rememberString(),
            titleSuffix = "(${nnvl.data.id})",
            onUp = { nav.up() },
            actions = buildList {

                if (nnvl.state != CampaignState.DRAFT && nnvl.data.isCommercial) {
                    add(ToolbarAction.chart(Res.string.layer_top_detail__action_report) {
                        nav.navTo_ReportCampaignUI(
                            ReportCampaignArgs(
                                companyId = args.companyId,
                                campaignGroupId = nnvl.data.groupId,
                                withBackNav = true,
                            )
                        )
                    })
                }

                if ((nnvl.canDelete && nnvl.state in arrayOf(CampaignState.STOPPED, CampaignState.FINISHED))
                    || ((nnvl.canAdd || nnvl.canDelete) && nnvl.state == CampaignState.DRAFT)
                ) {
                    add(ToolbarAction.delete(Res.string.layer_top_detail__delete_title) { deleteState.show() })
                }


                if (nnvl.canCreate
                    && nnvl.canEdit
                    && nnvl.state in arrayOf(CampaignState.ACTIVE, CampaignState.PUBLISHED)
                ) {
                    add(ToolbarAction.pause(Res.string.layer_top_detail__pasuse__title) { pauseState.show() })
                }

                if (nnvl.canCreate
                    && nnvl.canEdit
                    && nnvl.state == CampaignState.PAUSED
                    && nnvl.data.end.date >= Clock.nowDate()
                ) {
                    add(ToolbarAction.resume(Res.string.layer_top_detail__continue__title) { resumeState.show() })
                }

                if (nnvl.canCreate
                    && nnvl.canEdit
                    && nnvl.state in arrayOf(CampaignState.ACTIVE, CampaignState.PUBLISHED, CampaignState.PAUSED)
                ) {
                    add(ToolbarAction.stop(Res.string.layer_top_detail__terminate__title) { stopState.show() })
                }


                if (nnvl.canEdit && nnvl.state != CampaignState.DELETED) {
                    add(ToolbarAction.edit(Res.string.layer_top_detail__edit_start__title) { editState.show() })
                }

            }
        )
        LazyVerticalStaggeredGrid(
            columns = StaggeredGridCells.FixedSize(512.dp),
            contentPadding = PaddingValues(vertical = 16.dp),
            horizontalArrangement = Arrangement.SpaceEvenly,
            verticalItemSpacing = 16.dp,
            modifier = Modifier.fillMaxSize()
        ) {
            item {
                CardMainInfo(nnvl)
            }
            item {
                CardVisuals(nnvl)
            }
            item {
                CardDevices(nnvl)
            }
            item {
                CardOffsets(nnvl)
            }
            items(nnvl.visualsDynamic) {
                CardVisualDynamic(nnvl, it)
            }
            items(nnvl.applications) {
                CardApplications(nnvl, it)
            }
            nnvl.data.miniApp.firstOrNull { it.enabled }?.also { miniApp ->
                item {
                    CardMiniApp(miniApp)
                }
            }
        }
    }
}


@Composable
private fun CardMainInfo(nnvl: Wrapper) {
    CampaignInfoCardMain(
        type = CampaignType[nnvl.data.settings.type],
        name = nnvl.data.name,
        dtStart = nnvl.data.start.date,
        dtEnd = nnvl.data.end.date,
        volume = nnvl.data.volume.toFloat() / 100F,
        enableCompanyCommercial = nnvl.data.settings.enableCommercial,
        isCom = nnvl.data.isCommercial,
        isNCom = nnvl.data.isNonCommercial,
        countLocation = nnvl.data.locations.count { it.devices.any { it.isActive } },
        countDevice = nnvl.data.locations.sumOf { it.devices.count { it.isActive } },
        countVisuals = nnvl.visualsPrimary.size,
        impressionsSum = nnvl.data.impressionExpected,
        impDur = nnvl.data.settings.campaignDuration.milliseconds,
        impCount = nnvl.data.settings.campaignCount,
        impSpace = ((nnvl.data.settings.campaignDuration * nnvl.data.settings.campaignCount).toDouble() / 1.hours.inWholeMilliseconds.toDouble()).toFloat() * 100F,
        slotDur = (nnvl.data.settings.campaignCount * nnvl.data.settings.companyDuration).milliseconds,
        slotCount = nnvl.data.settings.campaignCount,
        slotCountCompany = nnvl.data.settings.companyCount,
        countApps = nnvl.applications.size,
        appBehavior = nnvl.data.appBehavior,
        visualRotaryMode = if (nnvl.data.visuals.size > 1) nnvl.data.visualRotaryMode else VisualRotaryMode.NONE.mask,

    )
}

@Composable
private fun CampaignPreviewScope.CardDevices(nnvl: Wrapper) {
    CampaignInfoCardDevices(
        showCounters = false,
        locations = nnvl.locations,
        onClickDevice = {
            nav.navTo_DevicePreviewUI(
                DeviceArgs(
                    deviceId = it
                )
            )
        },
        onClickLocation = {
            nav.navTo_LocationUI(
                LocationArgs(
                    companyId = args.companyId,
                    locationId = it
                )
            )
        }
    )

}

@Composable
private fun CampaignPreviewScope.CardVisuals(nnvl: Wrapper) {
    val clickAction: ((Long) -> Unit)? =
        if (nnvl.state == CampaignState.DELETED) null
        else {
            {
                nav.navTo_ResourceUI(
                    ResourceArgs(
                        companyId = args.companyId,
                        resourceId = it
                    )
                )
            }
        }
    CampaignInfoCardVisuals(
        visuals = nnvl.visualsPrimary,
        onClick = clickAction
    )
}

@Composable
private fun CampaignPreviewScope.CardVisualDynamic(nnvl: Wrapper, group: ImplCampaignInfoCard.VisualDynamic) {
    val clickAction: ((Long) -> Unit)? =
        if (nnvl.state == CampaignState.DELETED) null
        else {
            {
                nav.navTo_ResourceUI(
                    ResourceArgs(
                        companyId = args.companyId,
                        resourceId = it
                    )
                )
            }
        }
    CampaignInfoCardVisualsDynamic(
        group = group,
        onClick = clickAction
    )
}

@Composable
private fun CampaignPreviewScope.CardApplications(nnvl: Wrapper, config: ImplPlaylist.AppConfig) {
    val clickAction: ((Long) -> Unit)? =
        if (nnvl.state == CampaignState.DELETED) null
        else {
            {
                nav.navTo_AppDetailUI(
                    AppDetailArgs(
                        companyId = args.companyId,
                        appId = it
                    )
                )
            }
        }
    CampaignInfoCardApplication(
        config = config,
        visuals = nnvl.visualsPrimary,
        pictograms = nnvl.data.weatherPictograms,
        onClick = clickAction
    )
}

@Composable
private fun CardMiniApp(miniApp: common.frontend.api.Io.Campaign.MiniApp) {
    CampaignInfoCardMiniApp(miniApp)
}

@Composable
fun CampaignPreviewScope.DialogDelete(state: DialogState) {
    WarningDialog(
        state = state,
        title = Res.string.layer_top_detail__delete_title.rememberString(),
        message = Res.string.layer_top_detail__delete_message.rememberString(),
        confirmLabel = Res.string.dialog__action_delete.rememberString(),
    ) { action(CampaignPreviewVM.Action.Delete) }
}

@Composable
fun CampaignPreviewScope.DialogEdit(state: DialogState) {
    WarningDialog(
        state = state,
        title = Res.string.layer_top_detail__edit_start__title.rememberString(),
        message = Res.string.layer_top_detail__edit_start__message.rememberString(),
        confirmLabel = Res.string.dialog__action_edit.rememberString(),
    ) { action(CampaignPreviewVM.Action.Edit) }
}

@Composable
fun CampaignPreviewScope.DialogPause(state: DialogState) {
    WarningDialog(
        state = state,
        title = Res.string.layer_top_detail__pasuse__title.rememberString(),
        message = Res.string.layer_top_detail__pasuse__message.rememberString(),
        confirmLabel = Res.string.dialog__action_ok.rememberString(),
    ) { action(CampaignPreviewVM.Action.Pause) }
}

@Composable
fun CampaignPreviewScope.DialogResume(state: DialogState) {
    WarningDialog(
        state = state,
        title = Res.string.layer_top_detail__continue__title.rememberString(),
        message = Res.string.layer_top_detail__continue__message.rememberString(),
        confirmLabel = Res.string.dialog__action_ok.rememberString(),
    ) { action(CampaignPreviewVM.Action.Resume) }
}

@Composable
fun CampaignPreviewScope.DialogStop(state: DialogState) {
    WarningDialog(
        state = state,
        title = Res.string.layer_top_detail__terminate__title.rememberString(),
        message = Res.string.layer_top_detail__terminate__message.rememberString(),
        confirmLabel = Res.string.dialog__action_ok.rememberString(),
    ) { action(CampaignPreviewVM.Action.Stop) }
}